import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faRedo } from "@fortawesome/free-solid-svg-icons/faRedo";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons/faSyncAlt";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons/faTrashAlt";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import NoData from "../../../components/noData/noData";
import Table from "../../../components/table/coreUiTable/Table";
import {
  applyFilters,
  changePage,
  changeTab,
  clearFailed,
  closeJob,
  destroyJob,
  fetchJob,
  refresh,
  reloadJob,
  requeueFailed,
  requeueJob,
  requeuePending,
  setJobClassInput,
  setQueuesInput
} from "./redux/delayedJobsSagas";
import { formatRelativeTime } from "./formatRelativeTime";
import {
  type CountMap,
  type DelayedJobRecord,
  type OverviewCounts,
  type RootState,
  TABS
} from "./types";
import "./DelayedJobs.scss";

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

function ActionButton({
  label,
  icon,
  onClick,
  danger = false
}: {
  label: string;
  icon: IconDefinition;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={`icon-action${danger ? " danger" : ""}`}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}

function CountCards({ counts }: { counts: OverviewCounts }) {
  const cards: { key: keyof OverviewCounts; label: string }[] = [
    { key: "enqueued", label: "Enqueued" },
    { key: "working", label: "Working" },
    { key: "pending", label: "Pending" },
    { key: "failed", label: "Failed" }
  ];

  return (
    <div className="delayed-jobs__count-cards" data-testid="overview-counts">
      {cards.map((card) => (
        <div key={card.key} className="delayed-jobs__count-card">
          <span className="label">{card.label}</span>
          <span className="value">{counts[card.key] ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

function GroupTable({ title, rows }: { title: string; rows?: CountMap }) {
  const entries = Object.entries(rows || {});

  return (
    <section className="delayed-jobs__stats-section">
      <h3>{title}</h3>
      {entries.length === 0 ? (
        <NoData message="No data" />
      ) : (
        <Table
          table={{
            colHeaders: ["Name", "Count"],
            data: entries.map(([name, count]) => [name || "(blank)", count])
          }}
          pagination={false}
          sorter
          tableFilter={false}
        />
      )}
    </section>
  );
}

function JobDetail({
  job,
  onClose
}: {
  job: DelayedJobRecord | null;
  onClose: () => void;
}) {
  if (!job) return null;

  return (
    <div className="delayed-jobs__detail" data-testid="job-detail" role="dialog" aria-label="Job details">
      <div className="delayed-jobs__actions" style={{ justifyContent: "space-between" }}>
        <h3>Job #{job.id}</h3>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
      <dl className="delayed-jobs__detail-grid">
        <dt>Job class</dt>
        <dd>{formatValue(job.job_class)}</dd>
        <dt>Queue</dt>
        <dd>{formatValue(job.queue)}</dd>
        <dt>Priority</dt>
        <dd>{formatValue(job.priority)}</dd>
        <dt>Attempts</dt>
        <dd>{formatValue(job.attempts)}</dd>
        <dt>Run at</dt>
        <dd>{formatRelativeTime(job.run_at)}</dd>
        <dt>Locked at</dt>
        <dd>{formatRelativeTime(job.locked_at)}</dd>
        <dt>Locked by</dt>
        <dd>{formatValue(job.locked_by)}</dd>
        <dt>Failed at</dt>
        <dd>{formatRelativeTime(job.failed_at)}</dd>
        <dt>Created at</dt>
        <dd>{formatRelativeTime(job.created_at)}</dd>
      </dl>
      <h4>Job arguments</h4>
      <pre data-testid="job-arguments">{formatValue(job.job_arguments)}</pre>
      <h4>Last error</h4>
      <pre data-testid="last-error">{formatValue(job.last_error)}</pre>
    </div>
  );
}

export const DelayedJobsModule = () => {
  const dispatch = useDispatch();
  const {
    activeTab,
    queuesInput,
    jobClassInput,
    page,
    overview,
    records,
    pagination,
    isLoading,
    actionPending,
    error,
    selectedJob
  } = useSelector((state: RootState) => state.delayedJobs);

  React.useEffect(() => {
    dispatch(refresh());
  }, [dispatch]);

  const currentTab = TABS.find((tab) => tab.id === activeTab) || TABS[0];
  const isListTab = Boolean(currentTab.listStatus);

  const handleRemove = (jobId: number) => {
    if (!window.confirm(`Delete job #${jobId}? This cannot be undone.`)) return;
    dispatch(destroyJob(jobId));
  };

  const handleRequeueFailed = () => {
    if (!window.confirm("Requeue all failed jobs matching the current queue filter?")) return;
    dispatch(requeueFailed());
  };

  const handleClearFailed = () => {
    if (
      !window.confirm(
        "Clear all failed jobs matching the current queue filter? This cannot be undone."
      )
    ) {
      return;
    }
    dispatch(clearFailed());
  };

  const handleRequeuePending = () => {
    if (!window.confirm("Requeue all pending jobs matching the current queue filter?")) return;
    dispatch(requeuePending());
  };

  const renderList = () => {
    if (!records.length) return <NoData message="No delayed jobs found" />;

    return (
      <>
        <div className="delayed-jobs__table-wrap" data-testid="jobs-table">
          <Table
            table={{
              colHeaders: [
                "Job class",
                "Queue",
                "Priority",
                "Attempts",
                "Run at",
                "Locked by",
                "Failed at"
              ],
              data: records.map((job) => [
                job.job_class,
                formatValue(job.queue),
                formatValue(job.priority),
                formatValue(job.attempts),
                formatRelativeTime(job.run_at),
                formatValue(job.locked_by),
                formatRelativeTime(job.failed_at)
              ])
            }}
            extraColumns={[{ key: "actions", label: "Actions" }]}
            pagination={false}
            tableFilter={false}
            scopedSlots={{
              actions: (_item: unknown, index: number) => {
                const job = records[index];
                return (
                  <td>
                    <div className="delayed-jobs__row-actions">
                      <ActionButton label="View" icon={faEye} onClick={() => dispatch(fetchJob(job.id))} />
                      <ActionButton label="Retry" icon={faRedo} onClick={() => dispatch(requeueJob(job.id))} />
                      <ActionButton label="Reload" icon={faSyncAlt} onClick={() => dispatch(reloadJob(job.id))} />
                      <ActionButton
                        label="Remove"
                        icon={faTrashAlt}
                        danger
                        onClick={() => handleRemove(job.id)}
                      />
                    </div>
                  </td>
                );
              }
            }}
          />
        </div>

        <div className="delayed-jobs__pagination" data-testid="pagination">
          <button type="button" onClick={() => dispatch(changePage(page - 1))} disabled={page <= 1}>
            Previous
          </button>
          <span data-testid="page-state">
            Page {pagination.page} of {pagination.total_pages} ({pagination.total_count} total)
          </span>
          <button
            type="button"
            onClick={() => dispatch(changePage(page + 1))}
            disabled={page >= (pagination.total_pages || 1)}
          >
            Next
          </button>
        </div>
      </>
    );
  };

  const renderStats = () => (
    <div data-testid="stats-panel">
      <GroupTable title="By queue" rows={overview.by_queue} />
      <GroupTable title="By job class" rows={overview.by_job_class} />
      <GroupTable title="Working by job class" rows={overview.by_job_class_and_status?.working} />
      <GroupTable title="Pending by job class" rows={overview.by_job_class_and_status?.pending} />
      <GroupTable title="Failed by job class" rows={overview.by_job_class_and_status?.failed} />
    </div>
  );

  const renderOverview = () => (
    <>
      <CountCards counts={overview.counts} />
      {renderStats()}
    </>
  );

  const renderBody = () => {
    if (isLoading) return <LoadingAnimation />;

    if (activeTab === "overview") {
      return renderOverview();
    }

    return renderList();
  };

  return (
    <div id="admin-delayed-jobs" className="admin-delayed-jobs">
      <div className="tabs">
        <div className="tabs__list" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`tabs__tab ${activeTab === tab.id ? "tabs__tab--active" : ""}`}
              onClick={() => dispatch(changeTab(tab.id))}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="delayed-jobs__toolbar">
        <div className="delayed-jobs__filters">
          <div className="delayed-jobs__field">
            <label htmlFor="delayed-jobs-queues">Queues</label>
            <input
              id="delayed-jobs-queues"
              type="text"
              value={queuesInput}
              placeholder="comma-separated"
              onChange={(event) => dispatch(setQueuesInput(event.target.value))}
            />
          </div>
          <div className="delayed-jobs__field">
            <label htmlFor="delayed-jobs-job-class">Job class</label>
            <input
              id="delayed-jobs-job-class"
              type="text"
              value={jobClassInput}
              placeholder="substring"
              onChange={(event) => dispatch(setJobClassInput(event.target.value))}
            />
          </div>
          <button type="button" onClick={() => dispatch(applyFilters())}>
            Filter
          </button>
        </div>

        <div className="delayed-jobs__actions">
          <button type="button" onClick={() => dispatch(refresh())} disabled={isLoading || actionPending}>
            Refresh
          </button>
          {activeTab === "failed" && (
            <>
              <button type="button" onClick={handleRequeueFailed} disabled={actionPending}>
                Requeue Failed
              </button>
              <button type="button" onClick={handleClearFailed} disabled={actionPending}>
                Clear Failed
              </button>
            </>
          )}
          {activeTab === "pending" && (
            <button type="button" onClick={handleRequeuePending} disabled={actionPending}>
              Requeue Pending
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="delayed-jobs__error" role="alert" data-testid="action-error">
          {error}
        </div>
      )}

      {renderBody()}

      {!isLoading && isListTab && (
        <JobDetail job={selectedJob} onClose={() => dispatch(closeJob())} />
      )}
    </div>
  );
};
